import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Form, Formik } from "formik";
import Yup from "yup";
import { get, has, find, isEmpty, values as _values } from "lodash";
import { withStyles } from "@mui/styles";
import SubmitButton from "common/components/SubmitButton";
import {
  FormControl,
  FormHelperText,
  MenuItem,
  TextField,
  Select,
  Button,
  Snackbar,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import MuiAlert from "@mui/lab/Alert";
import SimpleSnackbar from "common/components/SimpleSnackbar/index";
import Resource from "common/components/Resource/index";
import { regionCodes, countryCodes, regions } from "common/location/index";
import { phoneNumberRegExp } from "common/utils/regexp";
import OperatingHours from "common/components/OperatingHours";
import { handleHours } from "common/helpers/formik";
import ImageUploader from "core/ImageUploader";
import EnhancedInput from "common/components/EnhancedInput";
import client from "common/admin/client";
import { FORM_SUBMISSION_ERROR } from "common/constants";
import resourceFactory from "common/admin/resourceFactory";
import Switch from "@mui/material/Switch";
import ProviderSelector from "./ProviderSelector";
const validationSchema = Yup.object().shape({
  name: Yup.string().required("Business name is required"),
  address: Yup.string().required("Street address is required"),
  city: Yup.string().required("City is required"),
  region: Yup.string()
    .required("Region is required")
    .oneOf(regionCodes, "Region is invalid"),
  country: Yup.string()
    .required("Country is required")
    .oneOf(countryCodes, "Country is invalid"),
  postal: Yup.string().required("Postal code is required"),
  phone: Yup.string().matches(
    phoneNumberRegExp,
    "Phone Number must match the following format: 555-555-5555",
  ),
  website: Yup.string(),
  community: Yup.number().required("Neighborhood is required"),
  building: Yup.number().nullable(),
  category: Yup.number().required("Category is required"),
  hours: Yup.object().nullable(),
  keywords: Yup.string(),
  description: Yup.string(),
  tenant_id: Yup.number()
    .notOneOf([0], "Tenant is required")
    .required("Tenant is required"),
});
function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}
const getCity = (community) => client.get(`cities/${community.city_id}`);

const BusinessForm = ({ classes, handleSubmit, initialValues, isEdit }) => {
  const [open, setOpen] = React.useState(false);
  const [providers, setProviders] = useState([]);
  const [providersData, setProvidersData] = useState(
    initialValues.food_links || [{ name: "", url: "", order_url: "" }],
  );
  const [selectedCommunity, setSelectedCommunity] = useState(null);
  const [tenants, setTenants] = useState([]);

  useEffect(() => {
    client
      .get("tenants")
      .then((data) => {
        const list = Array.isArray(data) ? data : data.data || [];
        setTenants(list);
      })
      .catch(console.error);
  }, []);

  const addProvider = () => {
    const newProvidersData = [...providersData];
    newProvidersData.push({ name: "", url: "", order_url: "" });
    setProvidersData(newProvidersData);
  };

  const onDelete = (index) => {
    const newProvidersData = providersData.filter(
      (_, currentIndex) => index !== currentIndex,
    );
    setProvidersData(newProvidersData);
  };
  const validateProviderData = () => {
    const isInValid = providersData.find(({ name, url }) => !name || !url);
    return !isInValid;
  };

  const providerChangeHandler = (event, index) => {
    const { name, value } = event.target;
    const newProvidersData = [...providersData];
    if (name === "provider") {
      const currentProvider = providers.find(({ id }) => id === value);

      newProvidersData[index] = { ...currentProvider };
      return setProvidersData(newProvidersData);
    }
    const currentProvider = newProvidersData[index];
    newProvidersData[index] = { ...currentProvider, [name]: value };
    return setProvidersData(newProvidersData);
  };
  useEffect(() => {
    const actions = resourceFactory("food_supplier_master");
    actions.getAll().then((data) => {
      setProviders(data);
    });
  }, []);
  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values, form, ...rest) => {
        const filteredData = providersData.filter(
          (element) => !_values(element).every(isEmpty),
        );
        if (values.category === 28 && filteredData.length) {
          const isValid = validateProviderData();
          if (!isValid) {
            form.setSubmitting(false);
            return setOpen(true);
          }
          return handleSubmit(values, form, providersData);
        }
        return handleSubmit(values, form, []);
      }}
    >
      {({
        values,
        errors,
        handleChange,
        isSubmitting,
        status,
        touched,
        setValues,
        setFieldValue,
        setFieldTouched,
      }) => (
        <Form>
          <Snackbar
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            open={open}
            autoHideDuration={6000}
            onClose={() => setOpen(false)}
          >
            <Alert severity="error">Provide valid Provider data</Alert>
          </Snackbar>
          <Grid container direction="column" spacing={2}>
            <Grid size={{ xs: 4 }}>
              <FormControl className={classes.formControl}>
                <TextField
                  error={touched.name && has(errors, "name")}
                  id="name"
                  type="text"
                  label="Business Name"
                  helperText={touched.name && get(errors, "name")}
                  onChange={handleChange}
                  value={values.name}
                />
              </FormControl>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <FormControl className={classes.formControl}>
                <FormHelperText>Active (display in directory)</FormHelperText>
                <Switch
                  id="active"
                  checked={values.active}
                  onChange={handleChange}
                  color="primary"
                />
                {touched.active && has(errors, "active") ? (
                  <FormHelperText>{errors.active}</FormHelperText>
                ) : null}
              </FormControl>
            </Grid>

            <Resource
              name="communities"
              queryParams={{ pagination: false }}
              render={(communities) => {
                const currentCommunity = communities.find(
                  (cCom) => cCom.id === values.community,
                );
                setSelectedCommunity(currentCommunity);
                return (
                  <>
                    <Grid size={{ xs: 4 }}>
                      <FormControl
                        className={classes.formControl}
                        error={touched.community && has(errors, "community")}
                      >
                        <Select
                          name="community"
                          id="community"
                          value={values.community}
                          onChange={async (e) => {
                            const community = communities.find(
                              (c) => c.id === e.target.value,
                            );
                            setSelectedCommunity(community);
                            const city = await getCity(community);
                            if (!values.city) {
                              setFieldValue("city", city.name);
                            }
                            if (!values.region) {
                              setFieldValue("region", city.region);
                              const region = find(
                                regions,
                                (r) => r.code === city.region,
                              );
                              if (region) {
                                setFieldValue("country", region.countryCode);
                              }
                            }
                            setFieldValue("community", community.id);
                          }}
                        >
                          {communities.map((community) => (
                            <MenuItem value={community.id} key={community.id}>
                              {community.name}
                            </MenuItem>
                          ))}
                        </Select>
                        {touched.community && has(errors, "community") ? (
                          <FormHelperText>{errors.community}</FormHelperText>
                        ) : (
                          <FormHelperText>Neighborhood</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    {selectedCommunity?.multilingual !== null &&
                      selectedCommunity?.multilingual !== undefined && (
                        <Grid size={{ xs: 4 }}>
                          <FormControl className={classes.formControl}>
                            <Select
                              name="multilingual"
                              id="multilingual"
                              value={selectedCommunity.multilingual}
                              disabled={true}
                            >
                              <MenuItem value={false} key="English">
                                English
                              </MenuItem>
                              <MenuItem value={true} key="Bilingual">
                                Bilingual
                              </MenuItem>
                            </Select>
                            <FormHelperText>Language Preference</FormHelperText>
                          </FormControl>
                        </Grid>
                      )}
                  </>
                );
              }}
            />
            <Grid size={{ xs: 4 }}>
              <FormControl
                className={classes.formControl}
                error={touched.building && has(errors, "building")}
              >
                <Resource
                  name="buildings"
                  queryParams={
                    values.community
                      ? { pagination: false, community_id: values.community }
                      : { pagination: false }
                  }
                  render={(buildings) => (
                    <Select
                      name="building"
                      id="building"
                      value={values.building}
                      onChange={(e) => {
                        if (e.target.value === null) {
                          setFieldValue("building", null);
                          setFieldValue("tenant_id", 0);
                          return;
                        }

                        const building = buildings.find(
                          (b) => b.id === e.target.value,
                        );
                        if (!values.address) {
                          setFieldValue("address", building.street_address);
                        }
                        if (!values.postal) {
                          setFieldValue("postal", building.postal_code);
                        }
                        if (isEmpty(values.hours)) {
                          setFieldValue("hours", building.hours);
                        }
                        setFieldValue("building", building.id);
                        if (building.tenant_id) {
                          setFieldValue("tenant_id", building.tenant_id);
                        }
                      }}
                    >
                      <MenuItem value={null}>
                        <em>None</em>
                      </MenuItem>
                      {buildings.map((building) => (
                        <MenuItem value={building.id} key={building.id}>
                          {building.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                {touched.building && has(errors, "building") ? (
                  <FormHelperText>{errors.building}</FormHelperText>
                ) : (
                  <FormHelperText>Building</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <FormControl
                className={classes.formControl}
                error={touched.tenant_id && has(errors, "tenant_id")}
              >
                <Select
                  value={values.tenant_id || ""}
                  name="tenant_id"
                  id="tenant_id"
                  onChange={(e) => {
                    setFieldValue("tenant_id", e.target.value);
                  }}
                  disabled={!!values.building}
                >
                  {tenants.map((tenant) => (
                    <MenuItem value={tenant.id} key={tenant.id}>
                      {tenant.display_name}
                    </MenuItem>
                  ))}
                </Select>
                {touched.tenant_id && has(errors, "tenant_id") ? (
                  <FormHelperText>{errors.tenant_id}</FormHelperText>
                ) : (
                  <FormHelperText>Tenant</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <FormControl
                className={classes.formControl}
                error={touched.category && has(errors, "category")}
              >
                <Resource
                  name="categories"
                  queryParams={{ pagination: false }}
                  render={(categories) => (
                    <Select
                      name="category"
                      id="category"
                      value={values.category}
                      onChange={handleChange}
                    >
                      {categories.map((category) => (
                        <MenuItem value={category.id} key={category.id}>
                          {category.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                {touched.category && has(errors, "category") ? (
                  <FormHelperText>{errors.category}</FormHelperText>
                ) : (
                  <FormHelperText>Category</FormHelperText>
                )}
              </FormControl>
            </Grid>
            {values.category === 28 ? (
              <>
                {providersData.map((provider, index) => {
                  return (
                    <ProviderSelector
                      providers={providers}
                      provider={provider}
                      handleChange={(event) =>
                        providerChangeHandler(event, index)
                      }
                      onDelete={() => onDelete(index)}
                    ></ProviderSelector>
                  );
                })}
                <Button
                  variant="contained"
                  color="primary"
                  style={{ width: 150, marginTop: 20 }}
                  onClick={addProvider}
                >
                  Add Provider
                </Button>
              </>
            ) : null}
            <Grid size={{ xs: 4 }}>
              <FormControl className={classes.formControl}>
                <TextField
                  error={touched.address && has(errors, "address")}
                  id="address"
                  type="text"
                  label="Street Address"
                  helperText={touched.address && get(errors, "address")}
                  onChange={handleChange}
                  value={values.address}
                />
              </FormControl>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <FormControl className={classes.formControl}>
                <TextField
                  error={touched.city && has(errors, "city")}
                  id="city"
                  type="text"
                  label="City"
                  helperText={touched.city && get(errors, "city")}
                  onChange={handleChange}
                  value={values.city}
                />
              </FormControl>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <FormControl
                className={classes.formControl}
                error={touched.region && has(errors, "region")}
              >
                <Select
                  name="region"
                  id="region"
                  value={values.region}
                  onChange={(e) => {
                    const region = find(
                      regions,
                      (r) => r.code === e.target.value,
                    );
                    region &&
                      setValues({ ...values, country: region.countryCode });
                    return handleChange(e);
                  }}
                >
                  {regions.map((region) => (
                    <MenuItem value={region.code} key={region.code}>
                      {region.name}
                    </MenuItem>
                  ))}
                </Select>
                {touched.region && has(errors, "region") ? (
                  <FormHelperText>{errors.region}</FormHelperText>
                ) : (
                  <FormHelperText>Region</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <FormControl className={classes.formControl}>
                <TextField
                  error={touched.postal && has(errors, "postal")}
                  id="postal"
                  type="text"
                  label="Postal Code"
                  onChange={handleChange}
                  helperText={touched.postal && get(errors, "postal")}
                  value={values.postal}
                />
              </FormControl>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <FormControl className={classes.formControl}>
                <TextField
                  error={touched.phone && has(errors, "phone")}
                  id="phone"
                  type="text"
                  label="Phone Number"
                  helperText={touched.phone && get(errors, "phone")}
                  onChange={handleChange}
                  value={values.phone}
                />
              </FormControl>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <FormControl className={classes.formControl}>
                <TextField
                  error={touched.website && has(errors, "website")}
                  id="website"
                  type="text"
                  label="Website"
                  helperText={touched.website && get(errors, "website")}
                  onChange={handleChange}
                  value={values.website}
                />
              </FormControl>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <FormControl className={classes.formControl}>
                <EnhancedInput
                  error={touched.keywords && has(errors, "keywords")}
                  id="keywords"
                  type="text"
                  label="Search Keywords"
                  multiline
                  rows="3"
                  onChange={handleChange}
                  helperText={touched.keywords && get(errors, "keywords")}
                  value={values.keywords}
                />
              </FormControl>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <FormControl className={classes.formControl}>
                <EnhancedInput
                  error={touched.description && has(errors, "description")}
                  id="description"
                  type="text"
                  label="About This Business"
                  multiline
                  rows="3"
                  onChange={handleChange}
                  helperText={touched.description && get(errors, "description")}
                  value={values.description}
                />
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <OperatingHours
                hours={values.hours}
                handleChange={handleHours(setFieldValue)}
              />
            </Grid>
            <Grid size={{ xs: 10 }}>
              <ImageUploader
                buttonId="logo_image_uploader_btn"
                inputId="logo_image_input"
                caption="Recommended dimensions 150px x 150px"
                initialImageUrl={values.logoUrl}
                touched={touched.logoFile}
                onChange={(file) => {
                  setFieldTouched("logoFile", true);
                  setFieldValue("logoFile", file, false);
                }}
              />
            </Grid>
            <Grid size={{ xs: 10 }}>
              <ImageUploader
                buttonId="hero_image_uploader_btn"
                inputId="hero_image_input"
                label="Upload Hero"
                caption="Recommended dimensions 1125px x 639px"
                initialImageUrl={values.heroUrl}
                touched={touched.heroFile}
                onChange={(file) => {
                  setFieldTouched("heroFile", true);
                  setFieldValue("heroFile", file, false);
                }}
              />
            </Grid>
          </Grid>
          <Grid size={{ xs: 4 }}>
            <SubmitButton isSubmitting={isSubmitting}>
              {isEdit ? "Update Business" : "Create Business"}
            </SubmitButton>
            {has(errors, "http") && (
              <SimpleSnackbar message={FORM_SUBMISSION_ERROR} />
            )}
          </Grid>
          {get(status, "success") && (
            <SimpleSnackbar
              message={isEdit ? "Business Updated!" : "Business Created!"}
            />
          )}
        </Form>
      )}
    </Formik>
  );
};

BusinessForm.propTypes = {
  classes: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  initialValues: PropTypes.object.isRequired,
  isEdit: PropTypes.bool,
};

const styles = (theme) => ({
  button: {
    marginTop: theme.spacing(2),
  },
  formControl: {
    margin: theme.spacing(1),
    width: "100%",
  },
});

export default withStyles(styles)(BusinessForm);
